import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Car, VideoCamera, ArrowRight } from "@phosphor-icons/react";

/**
 * Modal para derivar un expediente entre Videoperitación ↔ Presencial.
 *
 * Props:
 *  - expediente: doc del expediente (necesita extracted_data.tipo_encargo,
 *    perito_asignado_id y extracted_data.provincia).
 *  - peritos: lista completa de peritos (la usa Review.jsx)
 *  - onClose: () => void
 *  - onDerived: (newTipo) => void   // callback tras éxito (para refrescar)
 */
export default function DerivarModal({ expediente, peritos = [], onClose, onDerived }) {
  const currentTipo = expediente?.extracted_data?.tipo_encargo || "";
  const isPresencial = currentTipo.toLowerCase().startsWith("presencial");
  const targetTipo = isPresencial ? "Videoperitación asegurado" : "Presencial";
  const directionLabel = isPresencial ? "Volver a videoperitación" : "Derivar a presencial";

  const [peritoId, setPeritoId] = useState(expediente?.perito_asignado_id || "");
  const [unassign, setUnassign] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sugerencia: peritos con domicilio en la provincia del riesgo aparecen primero
  const provincia = (expediente?.extracted_data?.provincia || "").toLowerCase();
  const peritosOrdenados = useMemo(() => {
    const norm = (s) => (s || "").toLowerCase();
    return [...peritos].sort((a, b) => {
      const aMatch = provincia && norm(a.direccion_fiscal).includes(provincia) ? -1 : 0;
      const bMatch = provincia && norm(b.direccion_fiscal).includes(provincia) ? -1 : 0;
      return aMatch - bMatch;
    });
  }, [peritos, provincia]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { to: isPresencial ? "videoperitacion" : "presencial" };
      if (!isPresencial && peritoId) body.perito_id = peritoId;
      if (isPresencial && unassign) body.unassign = true;
      const r = await api.post(`/expedientes/${expediente.id}/derivar`, body);
      toast.success(
        isPresencial
          ? "Expediente devuelto a videoperitación"
          : `Derivado a presencial${r.data.perito_asignado_name ? ` · ${r.data.perito_asignado_name}` : ""}`,
      );
      onDerived?.(r.data.tipo_encargo);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo derivar");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="derivar-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-orange-600">
              Cambio de tipo de encargo
            </div>
            <h2 className="text-lg font-semibold mt-0.5">{directionLabel}</h2>
          </div>
          <button onClick={onClose} className="text-muted hover:text-foreground" data-testid="derivar-close">
            <X className="w-5 h-5" weight="bold" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Visual del cambio */}
          <div className="flex items-center justify-center gap-3 bg-slate-50 border border-border rounded-lg p-4">
            <div className="flex flex-col items-center gap-1 opacity-60">
              {isPresencial
                ? <Car className="w-7 h-7 text-orange-600" weight="bold" />
                : <VideoCamera className="w-7 h-7 text-emerald-600" weight="bold" />}
              <span className="text-[11px] font-semibold">{currentTipo || "—"}</span>
            </div>
            <ArrowRight className="w-5 h-5 text-muted" weight="bold" />
            <div className="flex flex-col items-center gap-1">
              {isPresencial
                ? <VideoCamera className="w-7 h-7 text-emerald-600" weight="bold" />
                : <Car className="w-7 h-7 text-orange-600" weight="bold" />}
              <span className="text-[11px] font-semibold">{targetTipo}</span>
            </div>
          </div>

          {/* Sólo al derivar a presencial: selector de perito */}
          {!isPresencial && (
            <div>
              <label className="text-[11px] font-semibold text-muted block mb-1">
                Asignar perito (opcional · su domicilio será el origen de la ruta)
              </label>
              <select
                value={peritoId}
                onChange={(e) => setPeritoId(e.target.value)}
                className="w-full text-sm border border-border rounded-md bg-white px-2 py-2 hover:border-foreground"
                data-testid="derivar-perito-select"
              >
                <option value="">— Sin asignar —</option>
                {peritosOrdenados.map((p) => {
                  const matches = provincia && (p.direccion_fiscal || "").toLowerCase().includes(provincia);
                  return (
                    <option key={p.id} value={p.id}>
                      {matches ? "⭐ " : ""}{p.name || p.email}{p.direccion_fiscal ? "" : " (sin domicilio)"}
                    </option>
                  );
                })}
              </select>
              {provincia && (
                <div className="text-[10px] text-muted mt-1">
                  ⭐ = perito con domicilio en {expediente?.extracted_data?.provincia}
                </div>
              )}
            </div>
          )}

          {/* Al volver a videoperitación: opción de desasignar perito */}
          {isPresencial && expediente?.perito_asignado_id && (
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={unassign}
                onChange={(e) => setUnassign(e.target.checked)}
                className="mt-0.5"
                data-testid="derivar-unassign"
              />
              <span>
                Desasignar perito ({expediente.perito_asignado_name || "—"}) al volver a videoperitación.
              </span>
            </label>
          )}

          <p className="text-[11px] text-muted">
            {isPresencial
              ? "El expediente desaparecerá del planificador de Rutas."
              : "El expediente aparecerá en el planificador de Rutas para su programación presencial."}
          </p>
        </div>

        <footer className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-2 border border-border bg-white rounded-md hover:border-foreground"
            data-testid="derivar-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="derivar-confirm"
          >
            {saving ? "Guardando..." : directionLabel}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
